import React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Field,
  FieldLabel,
  Input,
} from "@salestrak/ui";
import { useLocalSearchParams, useRouter } from '#app/lib/hooks/router-compat';

import { useAuth } from "#app/lib/auth-context";
import { PASSWORD_MIN_LENGTH } from "#app/lib/auth-service";
import { useAcceptInvitation } from "#app/lib/hooks/use-accept-invitation";
import { useInvitationByToken } from "#app/lib/hooks/use-invitation-by-token";
import { GoogleSignInButton } from "./google-sign-in-button";

export function AcceptInviteForm() {
  const { token } = useLocalSearchParams<{ token?: string }>();
  const { acceptInvite, acceptInviteWithGoogle, signOut } = useAuth();
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);

  const { loading, details, invalid, invalidReason } =
    useInvitationByToken(token);
  const { acceptInvitation } = useAcceptInvitation();

  React.useEffect(() => {
    if (details?.name) setName(details.name);
  }, [details?.name]);

  const alreadyCreated = details?.alreadyCreated ?? false;

  const handleAccept = async () => {
    if (submitting || googlePending) return;
    setError("");
    if (!token) {
      setError("Invitation link is missing a token.");
      return;
    }
    if (!details) {
      setError("This invitation is no longer valid.");
      return;
    }
    if (!password.trim()) {
      setError("Password is required.");
      return;
    }
    if (!alreadyCreated) {
      if (!name.trim()) {
        setError("Full name is required.");
        return;
      }
      if (password.length < PASSWORD_MIN_LENGTH) {
        setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const { workspaceId } = await acceptInvite(
        token,
        password,
        alreadyCreated ? undefined : name.trim(),
      );
      await finishAcceptance(
        workspaceId,
        alreadyCreated ? details.name : name.trim(),
      );
    } catch (err: any) {
      setError(err.message ?? "Unable to accept invitation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Shared by both accept paths: run the core-side membership flip, handle
  // the cancelled-between-steps race, then land in the app. (If an admin
  // cancelled the invite between the auth-side token consume — which issues
  // JWTs — and this call, the workspace User entity is gone and the mutation
  // returns null. Show it instead of redirecting into an empty
  // workspace-select view holding tokens we can't use.)
  const finishAcceptance = async (workspaceId: string, displayName: string) => {
    const accepted = await acceptInvitation(workspaceId, displayName);
    if (!accepted) {
      await signOut();
      setError(
        "This invitation has been cancelled. Contact your workspace admin to request a new invite.",
      );
      return;
    }
    router.replace(alreadyCreated ? "/(app)/workspace-select" : "/");
  };

  const handleGoogleCode = async (code: string) => {
    setError("");
    // The button only renders once details (and therefore token) resolved —
    // this narrows types rather than guarding a reachable state.
    if (!token || !details) return;
    try {
      const { workspaceId, name: resolvedName } = await acceptInviteWithGoogle(
        token,
        code,
      );
      await finishAcceptance(
        workspaceId,
        resolvedName ?? details.name ?? name.trim(),
      );
    } catch (err: any) {
      setError(err.message ?? "Unable to accept invitation. Please try again.");
    }
  };

  if (invalid) {
    const expired = invalidReason === "expired";
    return (
      <Box direction="column" gap="xl">
        <Alert variant="error">
          <AlertTitle>
            {expired ? "Invitation expired" : "Invitation no longer valid"}
          </AlertTitle>
          <AlertDescription>
            {expired
              ? "This invitation link has expired. Contact your workspace admin to request a new invite."
              : "This invitation has been cancelled or is no longer active. Contact your workspace admin to request a new invite."}
          </AlertDescription>
        </Alert>
        <Button onClick={() => router.replace("/(auth)/login")}>
          Go to sign in
        </Button>
      </Box>
    );
  }

  const contextLine = details
    ? `${details.inviterName} invited you to join ${details.workspaceName}.`
    : null;

  const displayEmail = details?.email ?? "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleAccept();
  };

  return (
    <Box as="form" direction="column" gap="xl" onSubmit={handleSubmit}>
      {contextLine ? (
        <Alert variant="info">
          <AlertTitle>You're almost in</AlertTitle>
          <AlertDescription>{contextLine}</AlertDescription>
        </Alert>
      ) : null}

      {details ? (
        <GoogleSignInButton
          onError={setError}
          onCode={handleGoogleCode}
          disabled={submitting}
          onPendingChange={setGooglePending}
        />
      ) : null}

      <Box direction="column" gap="sm">
        <Field>
          <FieldLabel>Email</FieldLabel>
          <Input type="email" value={displayEmail} disabled readOnly />
        </Field>

        {alreadyCreated ? null : (
          <Field>
            <FieldLabel>Full Name</FieldLabel>
            <Input
              placeholder="John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              autoComplete="name"
              disabled={submitting || googlePending || !details}
            />
          </Field>
        )}

        <Field>
          <FieldLabel>Password</FieldLabel>
          <Input
            type="password"
            placeholder={
              alreadyCreated
                ? "Your password"
                : `At least ${PASSWORD_MIN_LENGTH} characters`
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus={alreadyCreated}
            autoComplete={alreadyCreated ? "current-password" : "new-password"}
            disabled={submitting || googlePending || !details}
          />
        </Field>

        {alreadyCreated ? null : (
          <Field>
            <FieldLabel>Confirm Password</FieldLabel>
            <Input
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting || googlePending || !details}
            />
          </Field>
        )}
      </Box>

      {error ? (
        <Alert variant="error">
          <AlertTitle>Unable to accept invitation</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Button
        type="submit"
        loading={submitting || loading}
        disabled={
          !details ||
          !password.trim() ||
          googlePending ||
          (!alreadyCreated &&
            (!name.trim() ||
              password.length < PASSWORD_MIN_LENGTH ||
              !confirmPassword.trim()))
        }
      >
        {alreadyCreated ? "Sign In & Accept Invitation" : "Accept Invitation"}
      </Button>
    </Box>
  );
}
